import { BOT_NOT_ADMITTED_EVENT, BotNotAdmittedEventDetail, BotNotAdmittedReason } from '../constants/botAdmission';

/** Calendar_sync's `bot_active` for the bot (ConversationPage dispatches it). */
export const BOT_ACTIVE_EVENT = 'bot-active';
/** The recall socket's `participant_join`: the bot is in the meeting. */
export const BOT_JOINED_MEETING_EVENT = 'bot-joined-meeting';

/** How the wait for the host to let the bot in ended. */
export type BotAdmissionOutcome =
  | { kind: 'admitted' }
  | { kind: 'notAdmitted'; reason: BotNotAdmittedReason }
  | { kind: 'cancelled' };

export class BotAdmissionTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`Bot took too long to be accepted into the meeting (timeout: ${Math.round(timeoutMs / 1000)}s)`);
    this.name = 'BotAdmissionTimeoutError';
  }
}

const botIdOf = (event: Event): string | undefined => (event as CustomEvent<{ botId?: string }>)?.detail?.botId;

/**
 * Wait until the bot `botId` is let into the meeting, or is not. Admitted: the
 * first of calendar `bot_active` (authoritative) and the recall socket's
 * `participant_join` (the safety net when that broadcast is missed), as on the
 * desktop apps. Not admitted: the bot_terminated that says so (owner decision
 * 2026-10-03). Cancelled: the rep's Cancel, through the function handed to
 * `onCancellable`. Rejects with BotAdmissionTimeoutError after `timeoutMs`, a
 * safety net past Recall's own waiting-room limit.
 */
export const waitForBotAdmission = (
  botId: string,
  timeoutMs: number,
  onCancellable: (cancel: () => void) => void,
): Promise<BotAdmissionOutcome> =>
  new Promise((resolve, reject) => {
    const handleAdmitted = (event: Event) => {
      if (botIdOf(event) === botId) finish({ kind: 'admitted' });
    };
    const handleNotAdmitted = (event: Event) => {
      if (botIdOf(event) !== botId) return;
      const reason = (event as CustomEvent<BotNotAdmittedEventDetail>)?.detail?.reason ?? 'unknown';
      finish({ kind: 'notAdmitted', reason });
    };
    const stopListening = () => {
      clearTimeout(timeout);
      window.removeEventListener(BOT_ACTIVE_EVENT, handleAdmitted);
      window.removeEventListener(BOT_JOINED_MEETING_EVENT, handleAdmitted);
      window.removeEventListener(BOT_NOT_ADMITTED_EVENT, handleNotAdmitted);
    };
    const finish = (outcome: BotAdmissionOutcome) => {
      stopListening();
      resolve(outcome);
    };
    const timeout = setTimeout(() => {
      stopListening();
      reject(new BotAdmissionTimeoutError(timeoutMs));
    }, timeoutMs);

    window.addEventListener(BOT_ACTIVE_EVENT, handleAdmitted);
    window.addEventListener(BOT_JOINED_MEETING_EVENT, handleAdmitted);
    window.addEventListener(BOT_NOT_ADMITTED_EVENT, handleNotAdmitted);
    onCancellable(() => finish({ kind: 'cancelled' }));
  });
